/**
 * Returns true when an element can be focused programmatically without
 * changing any attributes.
 *
 * @param {Element} element
 * @returns {boolean}
 */
function isProgrammaticallyFocusable(element) {
  if (!(element instanceof HTMLElement)) return false;

  // Any explicit tabindex makes the element focusable via .focus().
  if (element.hasAttribute('tabindex')) return true;

  // Native focusable elements have a non-negative tabIndex by default.
  return element.tabIndex >= 0;
}

/**
 * Selects the best element to focus within a hash target.
 * Prefers a heading inside the target (more meaningful for screen readers),
 * otherwise focuses the target element itself.
 *
 * @param {Element} target
 * @returns {HTMLElement | null}
 */
function getPreferredFocusElement(target) {
  if (!(target instanceof HTMLElement)) return;

  const heading = target.querySelector(
    'h1, h2, h3, h4, h5, h6, [role="heading"]'
  );
  if (heading instanceof HTMLElement) return heading;

  return target;
}

/**
 * Focuses the hash target on the next animation frame so the browser has a
 * chance to apply native hash scrolling first.
 *
 * @param {string} hash
 * @returns {void}
 */
let pendingHashFocusRafId = 0;

function focusHashWithRetries(hash) {
  if (!hash) return;

  // Cancel any pending focus so older navigations can't steal focus.
  if (pendingHashFocusRafId) {
    cancelAnimationFrame(pendingHashFocusRafId);
    pendingHashFocusRafId = 0;
  }

  pendingHashFocusRafId = requestAnimationFrame(() => {
    pendingHashFocusRafId = 0;
    focusHashTarget(hash);
  });
}

/**
 * Moves focus to the element targeted by the provided hash (e.g. `#section-id`).
 * Ensures the target is programmatically focusable by applying `tabindex="-1"`
 * when needed, and focuses using `preventScroll` to avoid scroll "jumps".
 *
 * @param {string} hash - A URL hash including the leading `#`.
 * @returns {boolean} True if an element was found and focused.
 */
export function focusHashTarget(hash) {
  if (!hash || hash === '#') return false;

  // Decode to support ids with escaped characters.
  const id = (() => {
    try {
      return decodeURIComponent(hash.slice(1));
    } catch {
      return hash.slice(1);
    }
  })();

  const selectorId = globalThis.CSS?.escape ? globalThis.CSS.escape(id) : id;
  const target = document.querySelector(`#${selectorId}`);
  if (!target) return false;

  const focusElement = getPreferredFocusElement(target);
  if (!focusElement) return false;

  if (!isProgrammaticallyFocusable(focusElement)) {
    focusElement.setAttribute('tabindex', '-1');
  }

  try {
    focusElement.focus({ preventScroll: true });
  } catch {
    focusElement.focus();
  }

  return document.activeElement === focusElement;
}

/**
 * Installs global focus management for in-page hash navigation.
 *
 * Why: hash links scroll, but do not reliably move keyboard/screen-reader focus.
 * This keeps focus aligned with the scrolled-to section without adding targets
 * to the normal tab order.
 *
 * Installed once per page (idempotent).
 *
 * @returns {void}
 */
export function installHashFocus() {
  if (globalThis.__woolHashFocusInstalled) return;
  globalThis.__woolHashFocusInstalled = true;

  const focusCurrentHashSoon = () => {
    const hash = globalThis.location?.hash;
    if (!hash) return;
    focusHashWithRetries(hash);
  };

  // Direct loads with a hash (or after navigation).
  queueMicrotask(() => {
    focusHashWithRetries(globalThis.location?.hash);
  });

  // Browser URL bar hash edits, back/forward.
  globalThis.addEventListener('hashchange', focusCurrentHashSoon);

  // Click-to-hash where hashchange may not fire (same-hash).
  document.addEventListener(
    'click',
    event => {
      const a = event.target?.closest?.('a[href^="#"]');
      if (!a) return;
      const href = a.getAttribute('href');
      if (!href || href === '#') return;
      focusHashWithRetries(href);
    },
    { capture: true }
  );
}
